/**
 * Pure, framework-free navigation logic for the betonijerry app bar + home
 * resolver. No React / MUI / API imports, so it's fully unit-testable.
 * Consumers: useNavSignals (role), HeaderActions (primary action),
 * AccountMenu (menu model + initials), HomeResolver (home path).
 */

export function deriveRole({ isAuthenticated, isOperator }) {
  if (!isAuthenticated) return "anon";
  return isOperator ? "operator" : "customer";
}

/**
 * Read a nav-probe result that was STAMPED with the token it was fetched for
 * ({ token, counts }). A stamp minted for a different token belongs to the
 * PREVIOUS identity — login, company switch, impersonate start/stop — so its
 * counts must never leak into the new session's role: that is what routed a
 * staffer's impersonated customer onto the operator-only /tarjouspyynnot.
 * `counts: null` is a settled result too (anon, probe error, not-a-provider).
 *
 * @returns {{counts: object|null, settled: boolean}} `settled` false means the
 *   probe for the CURRENT token has not landed yet — consumers must wait, not
 *   route.
 */
export function readStamp(stamp, token) {
  const fresh = !!stamp && stamp.token === token;
  return { counts: fresh ? stamp.counts : null, settled: fresh };
}

export function homePathFor({ role, hasActiveOrders, operatorNeedsActivation }) {
  if (role === "operator") return operatorNeedsActivation ? "/liity" : "/tarjouspyynnot";
  if (role === "customer" && hasActiveOrders) return "/valut";
  return "/";
}

export function primaryActionFor(role, { operatorNeedsActivation = false, t } = {}) {
  if (role === "operator") {
    if (operatorNeedsActivation) {
      return { key: "liity", label: t("common.nav.joinCta"), to: "/liity", icon: "requestQuote" };
    }
    return { key: "tarjouspyynnot", label: t("common.nav.myRequests"), to: "/tarjouspyynnot", icon: "requestQuote", badge: true };
  }
  // anon + customer both get the quote-request CTA
  return { key: "tilaa", label: t("common.nav.requestQuoteCta"), to: "/tilaa", icon: "requestQuote", cta: true };
}

export function displayName(person, t) {
  const full = [person?.personFirstName, person?.personLastName]
    .map((s) => (typeof s === "string" ? s.trim() : ""))
    .filter(Boolean)
    .join(" ");
  return full || person?.personEmail || t("common.nav.defaultUserName");
}

export function initials(person) {
  const f = typeof person?.personFirstName === "string" ? person.personFirstName.trim() : "";
  const l = typeof person?.personLastName === "string" ? person.personLastName.trim() : "";
  if (f && l) return (f[0] + l[0]).toUpperCase();
  if (f) return f[0].toUpperCase();
  const email = typeof person?.personEmail === "string" ? person.personEmail.trim() : "";
  if (email) return email[0].toUpperCase();
  return "?";
}

export function menuModelFor({
  role, hasSentRequests, person, companyName, customerCounts,
  companies = [], activeAsiakasId = null, supportUnread = 0,
  canImpersonate = false, isImpersonating = false, t,
}) {
  const header = { type: "header", name: displayName(person, t), email: person?.personEmail || null };
  const divider = { type: "divider" };
  // Self-service profile editor (opens PersonEditDialog from AccountMenu). An
  // `action`, not a `link`, so it doesn't disturb the operator link-order
  // assertions and never lands last (logout stays the final row).
  const profile = { type: "action", key: "profile", label: t("common.nav.myDetails"), icon: "person", action: "profile" };
  const logout = { type: "action", key: "logout", label: t("common.nav.logout"), icon: "logout", action: "logout" };
  // betoni.online staff (systemAdmin/roleManager) get an impersonation entry.
  // Hidden once a session is active (the banner owns "stop"). Server-enforced.
  const impersonateItem =
    canImpersonate && !isImpersonating
      ? { type: "action", key: "impersonate", label: t("common.nav.impersonateAction"), icon: "switchAccount", action: "impersonate" }
      : null;

  if (role === "operator") {
    const activeName =
      companies.find((c) => c.asiakasId === activeAsiakasId)?.asiakasNimi ||
      companyName || companies[0]?.asiakasNimi || null;

    const items = [];
    if (companies.length > 1) {
      // Multi-company operator: a single collapsed switch row showing the active
      // company; tapping it opens a picker modal (AccountMenu). This is the app's
      // only active-tenant switcher — switching re-scopes the TARJOUSPYYNNÖt inbox,
      // offers and Hallinta. "Asiointirooli" mirrors the OP/Fennoa/Vero wording.
      // No static company line on the header; the active name lives on this row.
      items.push(header);
      items.push({
        type: "companySwitch",
        key: "company-switch",
        label: t("common.nav.switchCompany"),
        activeName,
      });
    } else {
      // Single company: nothing to switch — just show it as a static line.
      items.push({ ...header, company: activeName });
    }
    items.push(divider);
    items.push(profile);
    if (impersonateItem) items.push(impersonateItem);
    items.push({ type: "link", key: "valut", label: t("common.nav.myOrders"), to: "/valut", icon: "list" });
    items.push({
      type: "link", key: "tuki", label: t("common.nav.support"), to: "/tuki", icon: "supportAgent",
      count: supportUnread > 0 ? supportUnread : undefined,
    });
    items.push({ type: "link", key: "tilaa", label: t("common.nav.requestQuoteCta"), to: "/tilaa", icon: "requestQuote" });
    items.push({ type: "link", key: "hallinta", label: t("common.nav.admin"), to: "/hallinta", icon: "settings" });
    items.push(divider);
    items.push(logout);
    return items;
  }

  // anon (unused — no menu rendered) + customer.
  // A user whose ACTIVE company isn't a provider lands in the customer branch
  // even when they administer provider companies too (role is derived from the
  // active company's provider probe). Without a switch row here they'd be unable
  // to reach the operator view at all — the switcher is otherwise operator-only.
  // So mirror the operator branch: >1 eligible company => collapsed switch row
  // (active name on the row, not the header); the picker modal lists them all.
  const items = [];
  if (companies.length > 1) {
    const activeName =
      companies.find((c) => c.asiakasId === activeAsiakasId)?.asiakasNimi ||
      companyName || companies[0]?.asiakasNimi || null;
    items.push(header);
    items.push({ type: "companySwitch", key: "company-switch", label: t("common.nav.switchCompany"), activeName });
  } else {
    items.push(header);
  }
  items.push(divider, profile);
  if (impersonateItem) items.push(impersonateItem);
  if (hasSentRequests) {
    const activeCount =
      (customerCounts?.open ?? 0) +
      (customerCounts?.pending_verification ?? 0) +
      (customerCounts?.accepted ?? 0);
    items.push({
      type: "link", key: "valut", label: t("common.nav.myOrders"), to: "/valut", icon: "list",
      count: activeCount > 0 ? activeCount : undefined,
    });
  }
  items.push(divider);
  items.push(logout);
  return items;
}
